// @ts-nocheck
/**
 * ボス生成システム
 *
 * 責務:
 * - ボスカードの選出（鍵カード優先）
 * - ボスステータス計算
 * - 取り巻き生成
 */

import { CardSelector } from './CardSelector';
import { LevelCalculator } from './LevelCalculator';

/**
 * ボス生成クラス
 */
class BossGenerator {
  /**
   * @param {Object[]} cardPool - 全カードデータ配列
   * @param {number} seed - ダンジョンシード
   * @param {number} maxFloor - 最大フロア数
   */
  constructor(cardPool, seed, maxFloor) {
    this.cardPool = cardPool;
    this.seed = seed;
    this.maxFloor = maxFloor;

    this.cardSelector = new CardSelector(cardPool, seed);
    this.levelCalculator = new LevelCalculator(maxFloor, seed);
  }

  /**
   * ボス遭遇を生成（ボス + 取り巻き）
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @returns {Object} { boss, minions }
   */
  generateBossEncounter(dungeon, floorNumber) {
    const boss = this._generateBoss(dungeon, floorNumber);
    const minions = this._generateMinions(dungeon, floorNumber, boss);

    return { boss, minions };
  }

  /**
   * ボスを生成
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @returns {Object} ボスデータ
   * @private
   */
  _generateBoss(dungeon, floorNumber) {
    const { key1, key2, enemyBias } = dungeon;
    const isFinalFloor = floorNumber === this.maxFloor;

    let bossCard = null;

    // 最終階層: 鍵カードからボス候補を優先
    if (isFinalFloor) {
      bossCard = this.cardSelector.getBossFromKeys(key1, key2);
    }

    // 最終階層でない、または鍵カードにモンスターがない場合はプールから選出
    if (!bossCard) {
      bossCard = this._selectBossFromPool(dungeon, floorNumber);
    }

    // 最終階層でカードプールにもなければ鍵カードにフォールバック
    if (!bossCard && isFinalFloor) {
      bossCard = this.cardSelector.getBossFromKeys(key1, key2);
    }

    // カードがない場合はフォールバック
    if (!bossCard) {
      return this._createFallbackBoss(dungeon, floorNumber);
    }

    // ボスデータを構築
    const level = this.levelCalculator.calculateLevel(floorNumber, 'boss');
    const rarity = this.levelCalculator.determineBossRarity(floorNumber);

    return this._createEnemyData(bossCard, {
      level,
      rarity,
      isBoss: true,
      isStrong: false,
      floorNumber,
    });
  }

  /**
   * 取り巻きを生成
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @param {Object} boss - ボスデータ
   * @returns {Object[]} 取り巻き配列
   * @private
   */
  _generateMinions(dungeon, floorNumber, boss) {
    const minionCount = this.levelCalculator.determineMinionCount(floorNumber);
    if (minionCount === 0) return [];

    const { key1, key2 } = dungeon;
    const keyAttributes = this._getKeyAttributes(key1, key2);

    // ボスより低いコスト範囲
    const bossCost = parseInt(boss.card?.cost) || 3;
    const costRange = {
      min: Math.max(1, bossCost - 2),
      max: Math.max(1, bossCost - 1),
    };

    const minions = [];

    for (let i = 0; i < minionCount; i++) {
      const candidates = this.cardSelector.selectCards({
        attributes: keyAttributes.length > 0 ? keyAttributes : undefined,
        costRange,
        excludeIds: [boss.cardId, ...minions.map(m => m.cardId)],
        count: 1,
      });

      if (candidates.length > 0) {
        const level = this.levelCalculator.calculateLevel(floorNumber, 'minion');
        const rarity = this.levelCalculator.getEnemyTypeRarity('minion');

        minions.push(this._createEnemyData(candidates[0], {
          level,
          rarity,
          isBoss: false,
          isStrong: false,
          floorNumber,
          index: i,
        }));
      }
    }

    return minions;
  }

  /**
   * 敵データを構築
   *
   * @param {Object} card - カードデータ
   * @param {Object} options - オプション
   * @returns {Object} 敵データ
   * @private
   */
  _createEnemyData(card, options) {
    const { level, rarity, isBoss, isStrong, floorNumber, index = 0 } = options;

    return {
      id: isBoss ? `boss_${floorNumber}` : `enemy_${floorNumber}_${index}`,
      cardId: card.id,
      card: { ...card },
      name: card.name,
      attribute: card.attribute,
      category: card.category,
      level,
      rarity,
      isBoss,
      isStrong,
      // 基本ステータス（レアリティ・レベル補正は後で適用）
      baseAttack: parseInt(card.attack) || 500,
      baseHp: parseInt(card.hp) || 500,
    };
  }

  /**
   * フォールバックボスを作成
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @returns {Object} フォールバックボスデータ
   * @private
   */
  _createFallbackBoss(dungeon, floorNumber) {
    const level = this.levelCalculator.calculateLevel(floorNumber, 'boss');
    const rarity = this.levelCalculator.determineBossRarity(floorNumber);

    return {
      id: `boss_${floorNumber}`,
      cardId: null,
      card: null,
      name: `階層${floorNumber}のボス`,
      attribute: dungeon.enemyBias || '闇',
      category: null,
      level,
      rarity,
      isBoss: true,
      isStrong: false,
      baseAttack: 500 + floorNumber * 50,
      baseHp: 800 + floorNumber * 80,
    };
  }

  /**
   * カードプールからボスを選出（段階的フォールバック付き）
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {number} floorNumber - フロア番号
   * @returns {Object|null} ボスカード
   * @private
   */
  _selectBossFromPool(dungeon, floorNumber) {
    const { key1, key2 } = dungeon;
    const costRange = this.cardSelector.getCostRange(floorNumber);
    const keyAttributes = this._getKeyAttributes(key1, key2);

    // 属性がない場合はnullを返す（フォールバックボスへ）
    if (keyAttributes.length === 0) {
      return null;
    }

    // Step 1: 属性 + コスト範囲（min〜max）でフィルタ
    const candidates = this.cardSelector.selectCards({
      attributes: keyAttributes,
      costRange,
      count: 1,
    });
    if (candidates.length > 0) {
      return candidates[0];
    }

    // Step 2: 属性 + コスト上限のみ（1〜max）でフィルタ（下限を解放）
    const relaxedCostRange = { min: 1, max: costRange.max };
    const relaxedCandidates = this.cardSelector.selectCards({
      attributes: keyAttributes,
      costRange: relaxedCostRange,
      count: 1,
    });
    if (relaxedCandidates.length > 0) {
      return relaxedCandidates[0];
    }

    // 該当なし → フォールバックボスへ
    return null;
  }

  /**
   * 鍵カードから属性リストを取得
   *
   * @param {Object} key1 - 鍵カード1
   * @param {Object} key2 - 鍵カード2
   * @returns {string[]} 属性配列
   * @private
   */
  _getKeyAttributes(key1, key2) {
    const attributes = [];
    if (key1?.attribute) attributes.push(key1.attribute);
    if (key2?.attribute && key2.attribute !== key1?.attribute) {
      attributes.push(key2.attribute);
    }
    return attributes;
  }
}

export default BossGenerator;
export { BossGenerator };
